import { ColumnsDirective, GridComponent, ColumnDirective, GridModel, TextAlign, DataResult } from '@syncfusion/ej2-react-grids';
import * as React from 'react';
import { useRef, forwardRef } from 'react';
import UpperMenu from '../Layouts/UpperMenu';
import { useTranslation } from 'react-i18next';
import { GridType } from '../../../Api/UsersMicroserviceClient';
import delay from '../Functions/Delay';
import { Box, useMediaQuery } from '@mui/material';
import GridCompactView from './GridCompactView';
import { DataManager, Predicate } from '@syncfusion/ej2-data';

export interface IGridComponentStatefullProps<T> extends Omit<GridModel, 'columns'> {
    gridType: GridType;
    canAdd?: boolean;
    canDelete?: boolean;
    children: React.ReactNode;
    id: string;
    style?: React.CSSProperties;
    data: T[];
    dataLoader?: () => Promise<T[]>;
    templateColumns?: { field: string, template: (props: any) => JSX.Element | undefined }[];
    headerTemplateColumns?: { field: string, headerTemplate: (props: any) => JSX.Element | undefined }[];
    getCompactTemplate?: (item: T, index: number) => JSX.Element;
    headingField?: {
        name: keyof T,
        type: 'string' | 'date'
    };
    additionalFields?: {
        name: keyof T
    }[];
    fieldsToFilter?: {
        name: keyof T,
        type: 'string' | 'date'
    }[];
    fieldsToSort?: { name: keyof T, displayName: string }[];
    allowCompactMode?: boolean;
    columnsXX: {
        field?: keyof T | undefined, headerText?: string | undefined, width?: string,
        minWidth?: string, maxWidth?: string, template?: (props: any) => JSX.Element | undefined,
        visible?: boolean, allowFiltering?: boolean, type?: string, textAlign?: TextAlign,
        showInColumnChooser?: boolean, displayAsCheckBox?: boolean, format?: string,
        headerTemplate?: (props: any) => JSX.Element | undefined, dataSource?: Object[] | DataManager | DataResult;
        foreignKeyField?: string, foreignKeyValue?: string, filterTemplate?: string, index?: number
    }[],
    moduleName: string;
    searchLabel: string;
    menuName: string;
    isSubdodavatel?: boolean;
    fromExternal?: boolean;
    isFavorite?: boolean;
    isSwitchOn?: boolean;
    switchOnText?: string;
    switchOffText?: string;
    canChangeGroup?: boolean;
    canToolbarGrid?: boolean;
    canDiarySearch?: boolean;
    canExcelExport?: boolean;
    canTrash?: boolean;
    canFavorite?: boolean;
    canSwitch?: boolean;

    addLabel?: string;
    addSecondLabel?: string;
    onMobileListItemClick?: (item: T) => void;
    onCreate?: (event: any) => void;
    onDelete?: (event: any) => void;
    onThrashClicked?: (event: any) => void;
    onFavorite?: (favorite: boolean) => void;
    onSwitchOn?: (switchOn: boolean) => void;
    maxListHeight?: number;
    defaultSearchValue?: string;

    icon?: React.ReactNode;
    height: string | number | undefined;

    onSearch?: (text: string) => void;

}

const GridComponentStatefull = forwardRef(<T,>(props: IGridComponentStatefullProps<T>, ref: React.Ref<GridComponent>) => {
    const innerRef = useRef<GridComponent>(null);
    const [columnsLocal, setColumnsLocal] = React.useState<React.ReactNode | null>(null);
    const { t } = useTranslation();
    const isRestoringState = useRef(false);
    React.useImperativeHandle(ref, () => innerRef.current!);

    const saveGridSettings = (clearFilter: boolean) => {
        let persistedGridSettings;
        // if (isRestoringState.current) return;
        persistedGridSettings = JSON.parse(innerRef?.current!.getPersistData());
        console.log('persistedGridSettings', persistedGridSettings);
        let gridColumns = innerRef.current!.getColumns();
        persistedGridSettings.columns.forEach((persistedColumn) => {
            const column = gridColumns.find((col) => col.field === persistedColumn.field);
            if (column) {
                persistedColumn.headerText = column.headerText;
                persistedColumn.width = column.width;
                persistedColumn.template = column.template;
                persistedColumn.minWidth = column.minWidth;
                persistedColumn.visible = column.visible;
            }
        });

        persistedGridSettings.groupSettings = {
            columns: innerRef.current!.groupSettings.columns,
            showDropArea: innerRef.current!.groupSettings.showDropArea,
            showGroupedColumn: innerRef.current!.groupSettings.showGroupedColumn,
            captionTemplate: null
        };

        if (clearFilter)
            persistedGridSettings.filterSettings = { type: 'Excel', ignoreAccent: true, enableCaseSensitivity: false, showFilterBarStatus: true, columns: [] };
        window.localStorage.setItem(props.id, JSON.stringify(persistedGridSettings));
        console.log('saving grid settings', persistedGridSettings);
    };
    const onceLoaded = useRef(false);
    React.useEffect(() => {
        if (onceLoaded.current) return;
        onceLoaded.current = true;
        const savedSettings = window.localStorage.getItem(props.id);
        if (savedSettings) {
            isRestoringState.current = true;
            console.log('restoring grid settings', JSON.parse(savedSettings));
            const settings = JSON.parse(savedSettings);
            let columnsNew: {
                field?: keyof T | undefined, headerText?: string | undefined, width?: string,
                minWidth?: string, template?: (props: any) => JSX.Element | undefined,
                visible?: boolean, allowFiltering?: boolean, type?: string, textAlign?: TextAlign,
                showInColumnChooser?: boolean, displayAsCheckBox?: boolean, format?: string,
                headerTemplate?: (props: any) => JSX.Element | undefined, dataSource?: Object[] | DataManager | DataResult;
                foreignKeyField?: string, foreignKeyValue?: string, filterTemplate?: string, index?: number
            }[] = [];
            /* for (let col of props.columnsXX) {

                let clNew = settings.columns.find((t: any) => t.field === col.field);

                if (clNew === undefined) {
                    columnsNew.push(col);
                }
                // else { columnsNew.push(col); }
            }
            for (let col of settings.columns) {
                let clNew = props.columnsXX.find((t: any) => t.field === col.field);
                if (clNew !== undefined) {
                    columnsNew.push(col);
                }
            } */
            const columnsMap = new Map();
            props.columnsXX.forEach(col => {
                columnsMap.set(col.field?.toString(), { ...col });
            });

            let orderedColumns: typeof props.columnsXX = [];
            if (settings.columns && Array.isArray(settings.columns)) {
                // Use the order from the saved settings
                orderedColumns = settings.columns
                    .filter(savedCol => {
                        const fieldKey = savedCol.field?.toString();
                        return columnsMap.has(fieldKey);
                    })
                    .map((savedCol, index) => {  // Add the index parameter
                        const fieldKey = savedCol.field?.toString();
                        return {
                            ...columnsMap.get(fieldKey),
                            ...savedCol,
                            // Explicitly set the index to match position in array
                            index: index,  // This is critical for order preservation
                            template: props.columnsXX.find(c => c.field === savedCol.field)?.template
                        };
                    });

                // Add any new columns that weren't in the saved settings
                const savedFields = new Set(orderedColumns.map(col => col.field?.toString()));
                props.columnsXX.forEach(col => {
                    if (!savedFields.has(col.field?.toString())) {
                        orderedColumns.push({ ...col });
                    }
                });
            }
            else {
                // Fallback to original columns
                orderedColumns = [...props.columnsXX];
            }

            orderedColumns.forEach(savedCol => {
                const fieldKey = savedCol.field?.toString();
                if (columnsMap.has(fieldKey)) {
                    columnsMap.set(fieldKey, {
                        ...columnsMap.get(fieldKey),
                        ...savedCol,
                        template: props.columnsXX.find(c => c.field === savedCol.field)?.template
                    });
                }
            });
            columnsNew = Array.from(columnsMap.values());
            console.log('columnsNew', columnsNew);
            setColumnsLocal(<ColumnsDirective>
                {columnsNew.map((colInner, index) => (
                    <ColumnDirective key={colInner.field?.toString() + '_' + index} {...colInner} width={colInner.width} template={props.templateColumns?.find(x => x.field === colInner.field)?.template} headerTemplate={props.headerTemplateColumns?.find(x => x.field === colInner.field)?.headerTemplate} dataSource={colInner.dataSource} foreignKeyField={colInner.foreignKeyField} foreignKeyValue={colInner.foreignKeyValue} index={colInner.index} />
                ))}
            </ColumnsDirective>);
            delay(200).then(() => {
                if (innerRef.current) {
                    innerRef.current.sortSettings = settings.sortSettings;
                    // innerRef.current.searchSettings = settings.searchSettings;
                    //innerRef.current.groupSettings = settings.groupSettings;
                    if (settings.groupSettings.columns.length > 0) {
                        setPivotEnabled(true);
                        innerRef.current.allowGrouping = true;
                    }
                    else {
                        setPivotEnabled(false);
                        innerRef.current.allowGrouping = false;
                    }
                    let orderedColumns = columnsNew.sort((a, b) =>
                        (a.index || 999) - (b.index || 999)
                    );

                    // Update each column individually instead of replacing the entire columns array
                    orderedColumns.forEach((col, index) => {
                        if (col.field && innerRef.current) {
                            const fieldName = col.field.toString();
                            const column = innerRef.current.getColumnByField(fieldName);

                            if (column) {
                                if (col.width) {
                                    column.width = col.width;
                                }
                                column.index = col.index ?? index;
                                column.visible = col.visible ?? true;
                            }
                        }
                    });

                    // Refresh the grid columns to apply changes
                    //innerRef.current.refreshColumns();

                    if (settings.groupSettings && Array.isArray(settings.groupSettings.columns)) {
                        // Clear existing grouping first
                        innerRef.current.groupSettings = { columns: [] };

                        // Then apply saved grouping with validation
                        setTimeout(() => {
                            // Remove any duplicates in the saved columns array
                            const uniqueGroupColumns = settings.groupSettings.columns.filter((column, index, self) =>
                                self.indexOf(column) === index
                            );

                            // Ensure all grouped columns exist in the current grid
                            const validGroupColumns = uniqueGroupColumns.filter(
                                column => columnsNew.some(col => col.field?.toString() === column)
                            );

                            innerRef.current!.groupSettings = {
                                ...settings.groupSettings,
                                columns: validGroupColumns
                            };

                            // Force refresh grouping
                            setTimeout(() => {
                                if (innerRef.current) {
                                    //innerRef.current.refreshColumns();
                                    innerRef.current.refreshHeader();
                                }
                            }, 300);
                        }, 100);
                    }

                    if (settings.filterSettings && settings.filterSettings.columns) {
                        try {
                            innerRef.current.clearFiltering();
                            setTimeout(() => {
                                const safeFilterSettings = {
                                    type: settings.filterSettings.type || 'Excel',
                                    showFilterBarStatus: settings.showFilterBarStatus,
                                    ignoreAccent: settings.filterSettings.ignoreAccent,
                                    enableCaseSensitivity: settings.filterSettings.enableCaseSensitivity,
                                    columns: settings.filterSettings.columns.map((c) => (c.type === 'boolean' ? new Predicate(c.field, c.operator, c.value, c.ignoreCase, c.ignoreCase, c.matchCase) : {
                                        field: c.field,
                                        operator: c.operator,
                                        value: c.value,
                                        matchCase: c.matchCase,
                                        predicate: c.predicate,
                                    })),
                                };

                                innerRef.current!.filterSettings = safeFilterSettings;
                                setTimeout(() => {
                                    setFiltered(safeFilterSettings.columns.length > 0);
                                }, 50);
                            }, 200);
                        } catch (error) {
                            console.error("Error restoring filter settings:", error);
                            innerRef.current.filterSettings = { type: 'Excel', columns: [] };
                        }
                    } else {
                        innerRef.current.filterSettings = { type: 'Excel', columns: [] };
                    }

                    delay(1000).then(() => {
                        isRestoringState.current = false;
                    });
                }
            });

        }
        else setColumnsLocal(<ColumnsDirective>
            {props.columnsXX.map((col, index) => (
                <ColumnDirective key={col.field?.toString()} {...col} width={col.width} template={col.template} dataSource={col.dataSource} foreignKeyField={col.foreignKeyField} foreignKeyValue={col.foreignKeyValue} />
            ))}
        </ColumnsDirective>);

    }, [props.columnsXX, props.templateColumns, props.headerTemplateColumns, innerRef.current, props.id, props.data]);

    React.useEffect(() => {
        if (onceLoaded.current) {
            setColumnsLocal(<ColumnsDirective>
                {props.columnsXX.map((col, index) => (
                    <ColumnDirective key={col.field?.toString() + '_' + index} {...col} width={col.width} template={col.template} dataSource={col.dataSource} foreignKeyField={col.foreignKeyField} foreignKeyValue={col.foreignKeyValue} />
                ))}
            </ColumnsDirective>);
        }
    }, [props.isSwitchOn]);

    const biggerThan1100 = useMediaQuery('(min-width:1100px)');

    React.useEffect(() => {
        if (innerRef.current) {
            innerRef.current.dataSource = props.data ?? [];
            innerRef.current?.refresh();

        }
    }, [props.data]);

    const [filtered, setFiltered] = React.useState(false);
    const [pivotEnabled, setPivotEnabled] = React.useState(false);


    function pivotSwitch() {
        if (innerRef !== undefined && innerRef.current !== undefined) {
            innerRef.current!.allowGrouping = !innerRef.current!.allowGrouping;
            setPivotEnabled(!pivotEnabled);
        }
        // var rowsSelected = grid.current!.getSelectedRecords();
        // console.log(rowsSelected);
    }
    return (
        <div >

            <Box style={{ display: (biggerThan1100 || !props.allowCompactMode) ? undefined : 'none' }}>
                <UpperMenu
                    moduleName={props.moduleName}
                    gridType={props.gridType}
                    canAdd={props.canAdd}
                    canDelete={props.canDelete}
                    filtered={filtered}
                    canTrash={props.canTrash}
                    onDelete={props.onDelete}
                    onFilterCleared={() => { saveGridSettings(true); }}
                    grid={innerRef}
                    gridId={props.id}
                    canChangeGroup={props.canChangeGroup}
                    canToolbarGrid={props.canToolbarGrid}
                    canDiarySearch={props.canDiarySearch}
                    menuName={props.menuName}
                    searchLabel={props.searchLabel}
                    canExcelExport={props.canExcelExport}
                    onCreate={props.onCreate}
                    onThrashClicked={props.onThrashClicked}
                    isSubdodavatel={props.isSubdodavatel}
                    addLabel={props.addLabel}
                    addSecondLabel={props.addSecondLabel}
                    defaultSearchValue={props.defaultSearchValue}
                    canFavorite={props.canFavorite}
                    isFavorite={props.isFavorite}
                    onFavorite={props.onFavorite}
                    canSwitch={props.canSwitch}
                    isSwitchOn={props.isSwitchOn}
                    switchOnText={props.switchOnText}
                    switchOffText={props.switchOffText}
                    onSwitchOn={props.onSwitchOn}
                    onSearch={props.onSearch}
                    pivotEnabled={pivotEnabled}
                    pivotSwitch={pivotSwitch}
                />
                {columnsLocal && <GridComponent
                    {...props}


                    id={props.id}
                    style={{ ...props.style, minHeight: '150px' }}
                    ref={innerRef}
                    height={props.height}
                    resizeStop={(e) => {
                        if (!isRestoringState.current)
                            saveGridSettings(false);
                    }}
                    actionComplete={(e) => {
                        if (isRestoringState.current) return;
                        console.log('actionComplete', e);
                        if (e.requestType === 'columnstate' || e.requestType === 'sorting' || e.requestType === 'filtering' || e.requestType === 'paging' || e.requestType === 'reorder' || e.requestType === 'grouping' || e.requestType === 'ungrouping') {
                            saveGridSettings(false);
                            if (e.requestType === 'filtering') {
                                if (e.action === 'clear-filter') {
                                    setFiltered(false);
                                }
                                else {
                                    setFiltered(true);
                                }
                            }
                        }
                        else if ((e.requestType === 'refresh' || e.requestType === 'filter') && e.name === 'actionComplete') {
                            console.log('filters', !!innerRef.current?.filterSettings?.columns?.length);
                            setFiltered(!!innerRef.current?.filterSettings?.columns?.length);
                        }

                    }}
                    searchSettings={{ ignoreCase: true, ignoreAccent: true }}
                >
                    {columnsLocal}
                    {props.children}

                </GridComponent>}
            </Box>
            {(!biggerThan1100 && props.allowCompactMode && (props.headingField || props.getCompactTemplate)) && <Box>
                <GridCompactView
                    maxListHeight={props.maxListHeight}
                    onMobileListItemClick={props.onMobileListItemClick}
                    additionalFields={props.additionalFields?.map(q => q.name) ?? []}
                    data={props.data}
                    getCompactTemplate={props.getCompactTemplate}
                    headingField={props.headingField}
                    propsToFilter={props.fieldsToFilter?.map(q => q) ?? []}
                    propsToSort={props.fieldsToSort?.map(q => q) ?? []}
                    searchPlaceholder={props.searchLabel}
                    noResultsText={t('noRecords')}
                />
            </Box>}
        </div>
    );
}) as <T>(
    props: IGridComponentStatefullProps<T> & { ref?: React.Ref<GridComponent> }
) => JSX.Element;

export default GridComponentStatefull;